import classnamesBind from 'classnames/bind';
import Cookies from 'universal-cookie';
import { detect } from 'detect-browser';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import MessageBar from '@concur/nui-widgets/lib/MessageBars/MessageBar';
import { withThemeStyles } from '@concur/ui-theming';
import withErrorBoundary from '../utils/withErrorBoundary';
import compose from '../utils/compose';
import withNuiFormatter from '../utils/withNuiFormatter';
import isFiori from '../utils/isFiori';
import { COOKIE_PREF_LEVEL } from '../constants';
import * as styles from './AppBanner-*.css';

const CSS_BLOCK = 'sapcnqr-app-banner';

const Banner = (props) => {
    const {
        brandingId,
        classNameMap,
        demo,
        formatter,
        themeName,
        useFullWidth,
        userPrefs,
        useSmallViewportBreakpoints,
    } = props;

    const classnames = classnamesBind.bind(classNameMap);

    const [showLegacyBrowserWarning, setShowLegacyBrowserWarning] = useState(false);
    const [userCookiePref, setUserCookiePref] = useState(0);
    const cookies = new Cookies();

    useEffect(() => {
        const browser = detect();

        const isUnsupportedBrowser = demo?.isIE ? true : browser?.name === 'ie' && parseInt(browser?.version, 10) < 11;

        // notice_preferences cookie: "0:", "1:", "2:"
        let noticePrefs = cookies.get('notice_preferences') ? cookies.get('notice_preferences').replace(/:/, '') : 0;
        noticePrefs = typeof demo?.cookieLevel === 'number' ? demo?.cookieLevel : parseInt(noticePrefs, 10);

        setUserCookiePref(noticePrefs);

        let isCookieSet = false;
        if (userCookiePref >= COOKIE_PREF_LEVEL.functional) {
            isCookieSet = cookies.get('hidebrowserwarning');
        }

        if (isUnsupportedBrowser && !isCookieSet) {
            setShowLegacyBrowserWarning(true);
        }
    }, []);

    const onLegacyBannerClose = () => {
        if (userCookiePref >= COOKIE_PREF_LEVEL.functional) {
            const date = new Date();
            // set expiration date to one week
            date.setTime(date.getTime() + 7 * 24 * 60 * 60 * 1000);
            cookies.set('hidebrowserwarning', 'y', { expires: date });
        }

        setShowLegacyBrowserWarning(false);
    };

    const classes = classnames(
        CSS_BLOCK,
        {
            [`${CSS_BLOCK}--full-width`]: useFullWidth || isFiori(brandingId, themeName),
            [`${CSS_BLOCK}--fully-responsive`]: useSmallViewportBreakpoints,
        },
    );

    const bannerClasses = classnames(`${CSS_BLOCK}__messagebar`);

    const closeProps = userCookiePref >= COOKIE_PREF_LEVEL.functional ? {
        onCloseClicked: onLegacyBannerClose,
        textClose: formatter.formattedMessage({ id: 'CoreUI.close' }),
    } : {};

    return (
        <div className={classes}>
            {showLegacyBrowserWarning
                    && (
                        <MessageBar
                            className={bannerClasses}
                            description={formatter.formattedMessage({ id: 'CoreUI.legacyBrowserWarningDescription' })}
                            onCloseClicked={closeProps.onCloseClicked}
                            text={formatter.formattedMessage({ id: 'CoreUI.legacyBrowserWarningText' })}
                            textClose={closeProps.textClose}
                            type="error"
                        />
                    )}
            {userPrefs?.isTestEntity
                    && (
                        <MessageBar
                            className={bannerClasses}
                            text={formatter.formattedMessage({ id: 'CoreUI.testEntityBannerText' })}
                        />
                    )}
            {userPrefs?.isTestUser
                    && (
                        <MessageBar
                            className={bannerClasses}
                            text={formatter.formattedMessage({ id: 'CoreUI.testUserBannerText' })}
                        />
                    )}
        </div>
    );
};

Banner.displayName = 'Banner';

Banner.cssBlock = CSS_BLOCK;

Banner.propTypes = {
    demo: PropTypes.shape({
        isIE: PropTypes.bool,
        cookieLevel: PropTypes.number,
    }),
    userPrefs: PropTypes.object,
    useFullWidth: PropTypes.bool,
    useSmallViewportBreakpoints: PropTypes.bool,
};

export default withThemeStyles(compose(
    withNuiFormatter,
    withErrorBoundary,
)(Banner), styles);
